import {
  Box,
  Button,
  ResponsiveDropdown,
  Typography,
  UserAvatar,
} from '@capitalise/ui-design-system';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import withOpenIntercom from 'HOC/withOpenIntercom';
import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import { useRef } from 'react';
import HeaderMenu from '../Header/HeaderMenu';

const IntercomLink = withOpenIntercom(Button);

const UserMenu = ({ name, email, onDemasquerade, logoutCallback }) => {
  const isLoading = !(name && email);
  const open = useRef(false);

  const intercomHandler = useCallback(() => {
    Analytics.track(AnalyticsEventCategory.user, AnalyticsEventAction.profileChange);
  }, []);

  const openHandler = useCallback(() => {
    Analytics.track(AnalyticsEventCategory.user, AnalyticsEventAction.profileExpand);
    open.current = !open.current;
  }, [open]);

  const logoutHandler = useCallback(() => {
    Analytics.track(AnalyticsEventCategory.user, AnalyticsEventAction.profileLogout);
    logoutCallback();
  }, []);

  return (
    <ResponsiveDropdown
      id="user-menu"
      size="large"
      onOpenCallback={openHandler}
      testId="user-menu"
      collapsedContent={
        <HeaderMenu label={name} color="green-500" size="small" noLabel open={open.current} />
      }
      expandedContent={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          paddingY="30px"
          data-testid="user-menu-profile"
          zIndex="103"
        >
          <Box marginBottom="20px">
            {isLoading && <Skeleton width="40px" height="40px" />}
            {!isLoading && <UserAvatar name={name} color="green-500" size="medium" />}
          </Box>
          <Typography size="body-18">{name || <Skeleton width="100px" />}</Typography>
          <Typography size="caption-12">{email || <Skeleton width="100px" />}</Typography>
          <Box mt="40px" mb="16px" textAlign="center">
            {isLoading && (
              <>
                <Skeleton width="200px" />
                <Skeleton width="150px" />
              </>
            )}
            {!isLoading && (
              <Typography size="body-14">
                To manage or cancel your account or if you need help, you can chat with Capitalise.
              </Typography>
            )}
          </Box>
          <Box mb="16px" width="100%">
            {isLoading && <Skeleton width="200px" height="40px" />}
            {!isLoading ? (
              onDemasquerade ? (
                <Button variant="outlined" baseColor="green-500" fullWidth onClick={onDemasquerade}>
                  Demasquerade
                </Button>
              ) : (
                <IntercomLink
                  variant="outlined"
                  baseColor="green-500"
                  fullWidth
                  event={intercomHandler}
                >
                  Request a change
                </IntercomLink>
              )
            ) : null}
          </Box>
          <Box width="100%">
            {isLoading && <Skeleton width="200px" height="40px" />}
            {!isLoading && (
              <Button baseColor="green-500" fullWidth onClick={logoutHandler}>
                Logout
              </Button>
            )}
          </Box>
        </Box>
      }
    />
  );
};

UserMenu.propTypes = {
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  email: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  logoutCallback: PropTypes.func,
  onDemasquerade: PropTypes.func,
  isMasquerade: PropTypes.bool,
};

export default memo(UserMenu);
